/**
 * Paint is represented as an append-only log of serializable stroke events.
 * The paint texture is always DERIVED by replaying this log — never the source
 * of truth. This module must stay free of three.js (and any rendering) imports
 * so a future network layer can ship these events over the wire unchanged.
 */

export type StrokeKind = 'spray' | 'footprint';

export interface StrokeEvent {
  kind: StrokeKind;
  locationId: string;
  /** Which paintable surface within the location (e.g. 'ground', 'pond', 'car-0'). */
  surfaceId: string;
  /** World-space hit position — stamps are projected by world distance, not UV. */
  pos: [number, number, number];
  /** UV at the hit, kept for analytics/minimap use and transport compactness later. */
  uv: [number, number];
  /** World-space stamp radius in meters. */
  radius: number;
  /** 0..1 per-stamp opacity (already includes distance falloff at emit time). */
  opacity: number;
  /** Hex color like '#ff5533'. Footprints use their fixed tint. */
  color: string;
  /** Seed for deterministic micro-jitter so replays are pixel-identical. */
  seed: number;
  /** Milliseconds since world epoch (location load / room creation later). */
  t: number;
}

export interface StrokeListener {
  (stroke: StrokeEvent): void;
}

/** Append-only stroke log for one world/session. */
export class StrokeLog {
  private events: StrokeEvent[] = [];
  private listeners = new Set<StrokeListener>();

  /** Append a stroke and notify consumers (the local paint system today;
   * a network transport tomorrow). */
  append(stroke: StrokeEvent): void {
    this.events.push(stroke);
    for (const l of this.listeners) l(stroke);
  }

  /** Subscribe to new strokes. Returns an unsubscribe function. */
  onStroke(listener: StrokeListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  all(): readonly StrokeEvent[] {
    return this.events;
  }

  get count(): number {
    return this.events.length;
  }

  clear(): void {
    this.events = [];
  }

  /** Replace the whole log (used by import). Does not notify listeners —
   * callers are expected to trigger a full replay. */
  replaceAll(events: StrokeEvent[]): void {
    this.events = events;
  }
}
